/**
 * Arena lighting.
 *
 * Two directional keys shaped like the window shafts, a cold ambient fill, and
 * a warm bounce off the floor. Light count is capped by the graphics preset —
 * the candles add their own point lights on top, and those are the first thing
 * dropped when quality goes down.
 */
import { useRef } from 'react';
import * as THREE from 'three';
import { useFrame, useThree } from '@react-three/fiber';
import { useSettings } from '../gameplay/settingsStore';
import { scene as bus } from '../scene/sceneBus';

export function Lighting() {
  const quality = useSettings((s) => s.quality);
  const keyRef = useRef<THREE.DirectionalLight>(null);
  const duelRef = useRef<THREE.PointLight>(null);
  const { scene } = useThree();

  // A dark hall with real depth: exponential fog plus a near-black background.
  if (!scene.fog) {
    // Light enough haze to give the hall depth, not so much that the far end
    // of the arena — and the projectiles crossing it — dissolves.
    scene.fog = new THREE.FogExp2(new THREE.Color('#111726'), 0.016);
    scene.background = new THREE.Color('#080a12');
  }

  useFrame(() => {
    // The duel light brightens with the danger in the room, so a heavy
    // exchange visibly lifts the whole arena.
    if (duelRef.current) {
      const target = 2.4 + bus.danger * 5;
      duelRef.current.intensity += (target - duelRef.current.intensity) * 0.12;
    }
  });

  return (
    <>
      <ambientLight color="#5c6d9e" intensity={0.85} />
      <hemisphereLight color="#9db6ff" groundColor="#3a2a1c" intensity={0.7} />

      <directionalLight
        ref={keyRef}
        color="#ffd2a0"
        intensity={2.9}
        position={[-14, 15, 8]}
        castShadow={quality.shadows}
        shadow-mapSize-width={quality.shadowMapSize}
        shadow-mapSize-height={quality.shadowMapSize}
        shadow-camera-near={1}
        shadow-camera-far={60}
        shadow-camera-left={-18}
        shadow-camera-right={18}
        shadow-camera-top={18}
        shadow-camera-bottom={-18}
        shadow-bias={-0.0012}
      />
      <directionalLight color="#6f8dff" intensity={1.35} position={[16, 12, -10]} />

      {/* Warm bounce over the duelling sigil; swells with the action. */}
      <pointLight
        ref={duelRef}
        color="#ffbb77"
        intensity={2.4}
        distance={28}
        decay={2}
        position={[0, 2.6, 0]}
      />
    </>
  );
}
